import { Injectable } from '@angular/core';
import { Router, NavigationEnd, NavigationStart } from '@angular/router';
import { filter } from 'rxjs/operators';
import { BehaviorSubject } from 'rxjs';
import { faI } from '@fortawesome/free-solid-svg-icons';

@Injectable({ providedIn: 'root' })
export class PreviousRouteService {
  private readonly storageKey = 'app_route_history';
  private history: string[] = [];
  private maxHistory = 50;

  public canGoBack$ = new BehaviorSubject<boolean>(false);

  constructor(private router: Router) {
    this.restoreHistory();

    this.router.events
      .pipe(filter(event => event instanceof NavigationStart))
      .subscribe((event: NavigationStart) => {
        console.log('Navigation started:', event.url);
      });

    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        const url = event.urlAfterRedirects;

        console.log(`NavigationEnd, url=${url}`);

        const normalizedUrl = this.normalizeUrl(url);

        const lastIndex = this.history.length - 1;
        const lastNormalized =
          lastIndex >= 0
            ? this.normalizeUrl(this.history[lastIndex])
            : null;

        if (lastNormalized === normalizedUrl) {
          // Same logical page → keep latest query params
          this.history[lastIndex] = url;
        } else {
          // New logical page
          console.log(`push url = ${url}`);
          this.history.push(url);
          console.log(this.history)
        }

        if (this.history.length > this.maxHistory) {
          this.history.shift();
        }

        this.persistHistory();
        this.updateCanGoBack();
      });
  }

  private restoreHistory(): void {
    const saved = sessionStorage.getItem(this.storageKey);

    if (saved) {
      try {
        this.history = JSON.parse(saved);
      } catch {
        this.history = [];
      }
    }

    // Ensure current route exists after refresh
    const currentUrl = this.router.url;

    if (!this.history.length) {
      this.history = [currentUrl];
    }

    this.persistHistory();
    this.updateCanGoBack();
  }

  private persistHistory(): void {
    sessionStorage.setItem(this.storageKey, JSON.stringify(this.history));
  }

  private normalizeUrl(url: string): string {
    return url.replace(/\?tab=\d+$/, '');
  }

  private updateCanGoBack(): void {
    this.canGoBack$.next(this.history.length > 1);
  }

  public canGoBack(steps: number = 1): boolean {
    return this.history.length > steps;
  }

  public goBack(steps: number = 1): string | null { 
    console.log('goBack')   
    if (!this.canGoBack(steps)) {
      return null;
    }

    const targetIndex = this.history.length - 1 - steps;
    const targetUrl = this.history[targetIndex];

    // Remove current page(s) from stack
    this.history.splice(targetIndex + 1);    

    console.log(`back, go to url = ${targetUrl}`);
    console.log(this.history)

    this.persistHistory();
    this.updateCanGoBack();

    this.router.navigateByUrl(targetUrl);

    return targetUrl;
  }

  public getPreviousUrl(): string | null {
    return this.canGoBack()
      ? this.history[this.history.length - 2]
      : null;
  }

  public getHistory(): string[] {
    return [...this.history];
  }

  // Remove edit page after successful save
  public onSaveEntity(): void {
    const currentUrl = this.router.url;

    if (this.history.length > 1) {
      const lastHistoryItem = this.history[this.history.length - 1];

      // extra precaution: remove only if current page matches last history item
      if (lastHistoryItem === currentUrl) {
        const removed = this.history.pop();
        console.log(`onSaveEntity, removed url=${removed}`);
        this.persistHistory();
        this.updateCanGoBack();
      } else {
        console.log(
          `onSaveEntity skipped, current=${currentUrl}, historyLast=${lastHistoryItem}`
        );
      }
    } else {
      console.log(`onSaveEntity, history.length=${this.history.length}`);
    }
  }

  // Remove edit page after canceling changes
  public onCancelChanges(): void {
    const currentUrl = this.router.url;

    if (this.history.length > 1) {
      const lastHistoryItem = this.history[this.history.length - 1];

      // extra precaution: remove only if current page matches last history item
      if (lastHistoryItem === currentUrl) {
        const removed = this.history.pop();
        console.log(`onCancelChanges, removed url=${removed}`);
        this.persistHistory();
        this.updateCanGoBack();
      } else {
        console.log(
          `onCancelChanges skipped, current=${currentUrl}, historyLast=${lastHistoryItem}`
        );
      }
    } else {
      console.log(`onCancelChanges, history.length=${this.history.length}`);
    }
  }

  // Optional helper if you ever need full reset
  public clearHistory(): void {
    this.history = [this.router.url];
    this.persistHistory();
    this.updateCanGoBack();
  }
}